import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';

const DAY = 24 * 60 * 60 * 1000;

const fmt = (iso: string, lang: string) => {
  try {
    return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

/**
 * A vendor's trial, for support (docs/pricing-v2.md): when it ends, and the
 * three things support does with it — give more days, end it, or reopen an
 * ended one. Shown when the vendor is on the trial plan or has a trial date.
 */
export function VendorTrialCard({
  vendorId,
  trialEndsAt,
  onChanged,
}: {
  vendorId: string;
  trialEndsAt?: string | null;
  onChanged: (trialEndsAt: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const plansApi = usePlansApi();
  const [busy, setBusy] = useState(false);

  const end = trialEndsAt ? new Date(trialEndsAt) : null;
  const expired = !!end && end.getTime() <= Date.now();
  const daysLeft = end && !expired ? Math.ceil((end.getTime() - Date.now()) / DAY) : 0;

  const run = async (body: { extendDays?: number; endNow?: boolean }, done: string) => {
    setBusy(true);
    try {
      const res: any = await plansApi.setVendorTrial(vendorId, body);
      onChanged(res?.trialEndsAt ? new Date(res.trialEndsAt).toISOString() : null);
      toast.success(done);
    } catch {
      toast.error(t('PRICING.ADMIN_TRIAL.ERROR'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={t('PRICING.ADMIN_TRIAL.TITLE')} description={t('PRICING.ADMIN_TRIAL.DESC')} testId="vendor-trial-card">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <span className={expired ? 'size-2 rounded-full bg-destructive' : end ? 'size-2 rounded-full bg-success' : 'size-2 rounded-full bg-muted-foreground'} aria-hidden />
          <p className="text-sm" data-testid="vendor-trial-status">
            {!end
              ? t('PRICING.ADMIN_TRIAL.NO_CLOCK')
              : expired
                ? t('PRICING.ADMIN_TRIAL.ENDED', { date: fmt(end.toISOString(), i18n.language) })
                : t('PRICING.ADMIN_TRIAL.RUNNING', { date: fmt(end.toISOString(), i18n.language), count: daysLeft })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy} data-testid="trial-extend-7" onClick={() => run({ extendDays: 7 }, t('PRICING.ADMIN_TRIAL.EXTENDED', { count: 7 }))}>
            {expired ? t('PRICING.ADMIN_TRIAL.REOPEN', { count: 7 }) : t('PRICING.ADMIN_TRIAL.EXTEND', { count: 7 })}
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => run({ extendDays: 14 }, t('PRICING.ADMIN_TRIAL.EXTENDED', { count: 14 }))}>
            {expired ? t('PRICING.ADMIN_TRIAL.REOPEN', { count: 14 }) : t('PRICING.ADMIN_TRIAL.EXTEND', { count: 14 })}
          </Button>
          {!expired && (
            <Button variant="outline" size="sm" disabled={busy} className="border-destructive/50 text-destructive hover:text-destructive" onClick={() => run({ endNow: true }, t('PRICING.ADMIN_TRIAL.ENDED_NOW'))}>
              {t('PRICING.ADMIN_TRIAL.END_NOW')}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{t('PRICING.ADMIN_TRIAL.HINT')}</p>
      </div>
    </SectionCard>
  );
}
